/**
 * SIM-1502 / SIM-1678 — field definitions section. Each row carries schema-connection state: a
 * qualified subline of every `dataset.table.column` match, a badge that is green when at least one
 * warehouse table holds the column and destructive when orphan, and per-pair `perTable` visibility
 * inline. `createPresetRef` lets the parent open the create sheet pre-filled.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  EmptyState,
  Input,
  Label,
  QueryView,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@simplicity/ui";
import { useImperativeHandle, useMemo, useRef, useState, type Ref } from "react";
import { notifyMutationError, notifyWarnings } from "./notify";
import { RevisionViewer } from "./RevisionViewer";
import {
  buildSchemaIndex,
  findFieldInTable,
  findFieldMatches,
  listUnmappedFields,
  type QualifiedColumnRef,
  type QualifiedTableRef,
  type SchemaIndex,
} from "./schemaConnection";
import type { AgentFieldDefinition, FieldBody, WarehouseSchema } from "./types";
import {
  useCreateFieldDef,
  useDeleteFieldDef,
  useFieldDefs,
  useUpdateFieldDef,
  useWarehouseSchema,
} from "./useDottieContext";

interface PerTableEntry {
  table: string;
  description: string;
}

interface FormState {
  fieldName: string;
  description: string;
  aliasesText: string; // comma-separated for the input; parsed on save.
  perTable: PerTableEntry[];
  tableName: string; // optional, used only by the sanity check
  reason: string;
}

const EMPTY_FORM: FormState = {
  fieldName: "",
  description: "",
  aliasesText: "",
  perTable: [],
  tableName: "",
  reason: "",
};

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

function asPerTable(value: unknown): PerTableEntry[] {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  const entries: PerTableEntry[] = [];
  for (const [table, description] of Object.entries(value as Record<string, unknown>)) {
    if (typeof description === "string") {
      entries.push({ table, description });
    }
  }
  return entries;
}

function fromFieldDef(row: AgentFieldDefinition): FormState {
  const body = row.body;
  return {
    fieldName: row.fieldName,
    description: asString(body.description),
    aliasesText: asStringArray(body.aliases).join(", "),
    perTable: asPerTable(body.perTable),
    tableName: "",
    reason: "",
  };
}

function buildBody(form: FormState, existing: FieldBody | undefined): FieldBody {
  const aliases = form.aliasesText
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const perTable: Record<string, string> = {};
  for (const e of form.perTable) {
    if (e.table.trim()) perTable[e.table.trim()] = e.description;
  }
  // Preserve any unknown fields on the existing row so editing this form
  // doesn't strip future schema additions.
  return {
    ...(existing ?? {}),
    description: form.description,
    aliases,
    perTable,
  };
}

function PerTableEditor({
  rows,
  onChange,
}: {
  rows: PerTableEntry[];
  onChange: (next: PerTableEntry[]) => void;
}) {
  return (
    <div className="space-y-2">
      {rows.map((row, i) => (
        <div key={`pt-${i}`} className="flex gap-2">
          <Input
            aria-label={`Per-table table name ${i + 1}`}
            placeholder="table_name"
            value={row.table}
            onChange={(e) => {
              const next = [...rows];
              next[i] = { ...row, table: e.target.value };
              onChange(next);
            }}
          />
          <Input
            aria-label={`Per-table description ${i + 1}`}
            placeholder="Override description for this table"
            value={row.description}
            onChange={(e) => {
              const next = [...rows];
              next[i] = { ...row, description: e.target.value };
              onChange(next);
            }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
            aria-label={`Remove per-table override ${i + 1}`}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange([...rows, { table: "", description: "" }])}
      >
        Add per-table override
      </Button>
    </div>
  );
}

function FieldEditorBody({
  form,
  onChange,
}: {
  form: FormState;
  onChange: (next: FormState) => void;
}) {
  return (
    <div className="mt-4 space-y-4">
      <div className="space-y-1">
        <Label htmlFor="fd-name">Field name</Label>
        <Input
          id="fd-name"
          // eslint-disable-next-line jsx-a11y/no-autofocus -- SIM-1680: sheet content is dialog-equivalent; AT pattern is to focus the first field on open.
          autoFocus
          value={form.fieldName}
          onChange={(e) => onChange({ ...form, fieldName: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="fd-description">Description</Label>
        <Textarea
          id="fd-description"
          rows={4}
          value={form.description}
          onChange={(e) => onChange({ ...form, description: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="fd-aliases">Aliases (comma-separated)</Label>
        <Input
          id="fd-aliases"
          value={form.aliasesText}
          onChange={(e) => onChange({ ...form, aliasesText: e.target.value })}
          placeholder="grade, grade_level, level"
        />
      </div>
      <div className="space-y-1">
        <div className="text-sm font-medium leading-none">Per-table overrides</div>
        <PerTableEditor
          rows={form.perTable}
          onChange={(perTable) => onChange({ ...form, perTable })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="fd-table">Associated table (optional)</Label>
        <Input
          id="fd-table"
          value={form.tableName}
          onChange={(e) => onChange({ ...form, tableName: e.target.value })}
          placeholder="Used only by the warehouse sanity check"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="fd-reason">Reason (optional)</Label>
        <Input
          id="fd-reason"
          value={form.reason}
          onChange={(e) => onChange({ ...form, reason: e.target.value })}
        />
      </div>
    </div>
  );
}

function FieldDefEditorSheet({
  open,
  onOpenChange,
  initial,
  title,
  submitLabel,
  onSubmit,
  pending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: FormState;
  title: string;
  submitLabel: string;
  onSubmit: (form: FormState) => void;
  pending: boolean;
}) {
  const [form, setForm] = useState<FormState>(initial);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            Field definitions tell Dottie what a column means and which tables it shows up in.
          </SheetDescription>
        </SheetHeader>
        <FieldEditorBody form={form} onChange={setForm} />
        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(form)} disabled={pending || form.fieldName.trim() === ""}>
            {submitLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function formatQualifiedColumns(matches: QualifiedColumnRef[]): string {
  return matches.map((m) => `${m.datasetId}.${m.tableName}.${m.columnName}`).join(", ");
}

function ConnectionBadge({ inSchema }: { inSchema: boolean }) {
  if (inSchema) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-md bg-success/10 px-2 py-0.5 text-xs text-success"
        data-testid="fd-conn-in-schema"
        aria-label="In warehouse schema"
      >
        <span aria-hidden="true">✓</span> in schema
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md bg-destructive/10 px-2 py-0.5 text-xs text-destructive"
      data-testid="fd-conn-orphan"
      aria-label="Not in any warehouse table"
    >
      <span aria-hidden="true">⚠</span> not in warehouse
    </span>
  );
}

function PerPairBadge({
  fieldName,
  tableName,
  match,
}: {
  fieldName: string;
  tableName: string;
  match: QualifiedTableRef | null;
}) {
  if (match) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-success"
        data-testid={`fd-pair-ok-${fieldName}-${tableName}`}
        aria-label={`Column ${fieldName} exists in ${match.datasetId}.${match.tableName}`}
      >
        <span aria-hidden="true">✓</span> exists in {match.datasetId}.{match.tableName}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-destructive"
      data-testid={`fd-pair-orphan-${fieldName}-${tableName}`}
      aria-label={`Column ${fieldName} not in ${tableName}`}
    >
      <span aria-hidden="true">⚠</span> not in {tableName}
    </span>
  );
}

function FieldNameCell({
  row,
  matches,
}: {
  row: AgentFieldDefinition;
  matches: QualifiedColumnRef[];
}) {
  const qualified = matches.length > 0 ? formatQualifiedColumns(matches) : null;
  return (
    <td>
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">{row.fieldName}</span>
          <ConnectionBadge inSchema={matches.length > 0} />
        </div>
        {qualified ? (
          <span
            className="font-mono text-xs text-muted-foreground"
            data-testid={`fd-qualified-${row.fieldName}`}
          >
            {qualified}
          </span>
        ) : (
          <span
            className="text-xs text-destructive"
            data-testid={`fd-qualified-orphan-${row.fieldName}`}
          >
            not in any warehouse table
          </span>
        )}
      </div>
    </td>
  );
}

function PerTablePairList({
  row,
  schemaIndex,
}: {
  row: AgentFieldDefinition;
  schemaIndex: SchemaIndex;
}) {
  const perTable = asPerTable(row.body.perTable);
  if (perTable.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1" data-testid={`fd-pairs-${row.fieldName}`}>
      {perTable.map((entry) => (
        <PerPairBadge
          key={entry.table}
          fieldName={row.fieldName}
          tableName={entry.table}
          match={findFieldInTable(row.fieldName, entry.table, schemaIndex)}
        />
      ))}
    </div>
  );
}

function FieldDefRow({
  row,
  schemaIndex,
  onEdit,
  onDelete,
  onHistory,
}: {
  row: AgentFieldDefinition;
  schemaIndex: SchemaIndex;
  onEdit: (row: AgentFieldDefinition) => void;
  onDelete: (row: AgentFieldDefinition) => void;
  onHistory: (row: AgentFieldDefinition) => void;
}) {
  const matches = findFieldMatches(row.fieldName, schemaIndex);
  return (
    <tr>
      <FieldNameCell row={row} matches={matches} />
      <td>
        <DescriptionCellInner text={asString(row.body.description)} />
        <PerTablePairList row={row} schemaIndex={schemaIndex} />
      </td>
      <td className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => onEdit(row)}>
          Edit
        </Button>
        <Button variant="outline" size="sm" onClick={() => onHistory(row)}>
          History
        </Button>
        <Button variant="destructive" size="sm" onClick={() => onDelete(row)}>
          Delete
        </Button>
      </td>
    </tr>
  );
}

// Variant of DescriptionCell that doesn't render its own <td>, so the
// PerTablePairList can sit beneath it in the same Description column.
function DescriptionCellInner({ text }: { text: string }) {
  if (!text) return null;
  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="block max-w-[28rem] truncate text-muted-foreground">{text}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-md whitespace-normal">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function FieldDefsTable({
  rows,
  schemaIndex,
  onEdit,
  onDelete,
  onHistory,
}: {
  rows: AgentFieldDefinition[];
  schemaIndex: SchemaIndex;
  onEdit: (row: AgentFieldDefinition) => void;
  onDelete: (row: AgentFieldDefinition) => void;
  onHistory: (row: AgentFieldDefinition) => void;
}) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No field definitions yet"
        description="Add the first column definition for this tenant."
      />
    );
  }
  return (
    <div className="admin-table-container">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Field name</th>
            <th>Description</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <FieldDefRow
              key={row.id}
              row={row}
              schemaIndex={schemaIndex}
              onEdit={onEdit}
              onDelete={onDelete}
              onHistory={onHistory}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function useFieldDefMutations(
  tenantId: string,
  editTarget: AgentFieldDefinition | null,
  deleteTarget: AgentFieldDefinition | null,
  closeCreate: () => void,
  closeEdit: () => void,
  closeDelete: () => void,
) {
  const createFieldDef = useCreateFieldDef(tenantId);
  const updateFieldDef = useUpdateFieldDef(tenantId);
  const deleteFieldDef = useDeleteFieldDef(tenantId);

  const handleCreate = (form: FormState) =>
    createFieldDef.mutate(
      {
        tenantId,
        fieldName: form.fieldName,
        body: buildBody(form, undefined),
        tableName: form.tableName || undefined,
        reason: form.reason || undefined,
      },
      {
        onSuccess: (res) => {
          notifyWarnings(res.warnings);
          closeCreate();
        },
        onError: (err) => notifyMutationError("Create field definition", err),
      },
    );

  const handleUpdate = (form: FormState) => {
    if (!editTarget) return;
    updateFieldDef.mutate(
      {
        id: editTarget.id,
        fieldName: form.fieldName,
        body: buildBody(form, editTarget.body),
        tableName: form.tableName || undefined,
        reason: form.reason || undefined,
      },
      {
        onSuccess: (res) => {
          notifyWarnings(res.warnings);
          closeEdit();
        },
        onError: (err) => notifyMutationError("Update field definition", err),
      },
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteFieldDef.mutate(
      { id: deleteTarget.id },
      {
        onSuccess: () => closeDelete(),
        onError: (err) => notifyMutationError("Delete field definition", err),
      },
    );
  };

  return {
    createFieldDef,
    updateFieldDef,
    deleteFieldDef,
    handleCreate,
    handleUpdate,
    handleDelete,
  };
}

const EMPTY_SCHEMA: WarehouseSchema = {
  fetchedAt: "",
  tenantId: "",
  error: null,
  datasets: [],
};

/**
 * SIM-1678 — handle exposed by `FieldDefinitionsSection` so the parent (or a
 * sibling like the Table section's "Add field def") can trigger the create
 * sheet with a per-table key pre-filled.
 */
export interface FieldDefinitionsSectionHandle {
  openCreateForTable: (tableName: string) => void;
}

interface FieldSectionBodyProps {
  fieldDefsQuery: ReturnType<typeof useFieldDefs>;
  schemaIndex: SchemaIndex;
  createOpen: boolean;
  setCreateOpen: (open: boolean) => void;
  createInitial: FormState;
  createKey: number;
  editTarget: AgentFieldDefinition | null;
  setEditTarget: (row: AgentFieldDefinition | null) => void;
  deleteTarget: AgentFieldDefinition | null;
  setDeleteTarget: (row: AgentFieldDefinition | null) => void;
  historyTarget: AgentFieldDefinition | null;
  setHistoryTarget: (row: AgentFieldDefinition | null) => void;
  handleAddClick: () => void;
  handleAddUnmapped: (column: QualifiedColumnRef) => void;
  handleCreate: (form: FormState) => void;
  handleUpdate: (form: FormState) => void;
  handleDelete: () => void;
  createPending: boolean;
  updatePending: boolean;
}

/**
 * SIM-1679 — crosswalk surface for fields. Lists warehouse columns without an
 * `AgentFieldDefinition`, one row per (dataset, table, column) triple so
 * collisions (same column name in multiple tables) are visible. Default-open
 * when the tenant has zero field defs.
 */
function UnmappedFieldsBlock({
  rows,
  defaultOpen,
  onAdd,
}: {
  rows: QualifiedColumnRef[];
  defaultOpen: boolean;
  onAdd: (column: QualifiedColumnRef) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (rows.length === 0) return null;
  return (
    <details
      className="rounded-md border border-border bg-muted/30 p-3"
      data-testid="fd-unmapped-block"
      open={open}
    >
      <summary
        className="cursor-pointer text-sm font-medium text-foreground"
        onClick={(e) => {
          e.preventDefault();
          setOpen((prev) => !prev);
        }}
      >
        Warehouse columns without a definition ({rows.length})
      </summary>
      <p className="mt-2 text-xs text-muted-foreground">
        These warehouse columns have no field definition yet. Add one to make them queryable.
      </p>
      <ul className="mt-3 space-y-1.5">
        {rows.map((c) => (
          <li
            key={`${c.datasetId}.${c.tableName}.${c.columnName}`}
            className="flex items-center justify-between gap-3 rounded border border-border bg-background px-3 py-1.5"
            data-testid={`fd-unmapped-row-${c.datasetId}.${c.tableName}.${c.columnName}`}
          >
            <div className="flex flex-col gap-0.5">
              <span className="font-medium text-foreground">{c.columnName}</span>
              <span className="font-mono text-xs text-muted-foreground">
                {c.datasetId}.{c.tableName} ({c.type})
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAdd(c)}
              data-testid={`fd-unmapped-add-${c.datasetId}.${c.tableName}.${c.columnName}`}
            >
              + Add definition
            </Button>
          </li>
        ))}
      </ul>
    </details>
  );
}

function FieldSectionBody({
  fieldDefsQuery,
  schemaIndex,
  createOpen,
  setCreateOpen,
  createInitial,
  createKey,
  editTarget,
  setEditTarget,
  deleteTarget,
  setDeleteTarget,
  historyTarget,
  setHistoryTarget,
  handleAddClick,
  handleAddUnmapped,
  handleCreate,
  handleUpdate,
  handleDelete,
  createPending,
  updatePending,
}: FieldSectionBodyProps) {
  const unmapped = useMemo(
    () => listUnmappedFields(fieldDefsQuery.data ?? [], schemaIndex),
    [fieldDefsQuery.data, schemaIndex],
  );
  const hasNoDefs = (fieldDefsQuery.data ?? []).length === 0;
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Field definitions</h2>
        <Button onClick={handleAddClick}>Add field</Button>
      </div>

      <UnmappedFieldsBlock rows={unmapped} defaultOpen={hasNoDefs} onAdd={handleAddUnmapped} />

      <QueryView
        query={fieldDefsQuery}
        error={(err) => (
          <p className="text-sm text-destructive" role="alert">
            Failed to load field definitions: {err.message}
          </p>
        )}
      >
        {(rows) => (
          <FieldDefsTable
            rows={rows}
            schemaIndex={schemaIndex}
            onEdit={setEditTarget}
            onDelete={setDeleteTarget}
            onHistory={setHistoryTarget}
          />
        )}
      </QueryView>

      <FieldDefEditorSheet
        key={`create-${createKey}`}
        open={createOpen}
        onOpenChange={setCreateOpen}
        initial={createInitial}
        title="New field definition"
        submitLabel="Create"
        onSubmit={handleCreate}
        pending={createPending}
      />

      <FieldDefEditorSheet
        key={editTarget?.id ?? "edit-none"}
        open={!!editTarget}
        onOpenChange={(open) => !open && setEditTarget(null)}
        initial={editTarget ? fromFieldDef(editTarget) : EMPTY_FORM}
        title="Edit field definition"
        submitLabel="Save"
        onSubmit={handleUpdate}
        pending={updatePending}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete field definition?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the row for this tenant. A revision is recorded so the prior content
              stays viewable in History.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RevisionViewer
        entityType="fieldDefinition"
        id={historyTarget?.id ?? null}
        open={!!historyTarget}
        onOpenChange={(open) => !open && setHistoryTarget(null)}
        title={historyTarget ? `History — ${historyTarget.fieldName}` : "History"}
      />
    </section>
  );
}

export function FieldDefinitionsSection({
  tenantId,
  createPresetRef,
}: {
  tenantId: string;
  createPresetRef?: Ref<FieldDefinitionsSectionHandle>;
}) {
  const fieldDefsQuery = useFieldDefs(tenantId);
  const warehouseQuery = useWarehouseSchema(tenantId);

  const [createOpen, setCreateOpen] = useState(false);
  const [createInitial, setCreateInitial] = useState<FormState>(EMPTY_FORM);
  const [editTarget, setEditTarget] = useState<AgentFieldDefinition | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AgentFieldDefinition | null>(null);
  const [historyTarget, setHistoryTarget] = useState<AgentFieldDefinition | null>(null);

  // Bump on each open so the create sheet remounts (its internal useState
  // initial is captured on mount). The key= remount handles form reset; we
  // intentionally do NOT use a setState-in-effect to clear createInitial on
  // close — every open path (handleAddClick + openCreateForTable) sets the
  // initial it wants, so leaked-state isn't possible.
  const createKeyRef = useRef(0);
  const [createKey, setCreateKey] = useState(0);

  const { createFieldDef, updateFieldDef, handleCreate, handleUpdate, handleDelete } =
    useFieldDefMutations(
      tenantId,
      editTarget,
      deleteTarget,
      () => setCreateOpen(false),
      () => setEditTarget(null),
      () => setDeleteTarget(null),
    );

  const schemaIndex = useMemo(
    () => buildSchemaIndex(warehouseQuery.data ?? EMPTY_SCHEMA),
    [warehouseQuery.data],
  );

  useImperativeHandle(
    createPresetRef,
    () => ({
      openCreateForTable(tableName: string) {
        setCreateInitial({
          ...EMPTY_FORM,
          perTable: [{ table: tableName, description: "" }],
        });
        createKeyRef.current += 1;
        setCreateKey(createKeyRef.current);
        setCreateOpen(true);
      },
    }),
    [],
  );

  const handleAddClick = () => {
    setCreateInitial(EMPTY_FORM);
    createKeyRef.current += 1;
    setCreateKey(createKeyRef.current);
    setCreateOpen(true);
  };

  const handleAddUnmapped = (column: QualifiedColumnRef) => {
    setCreateInitial({
      ...EMPTY_FORM,
      fieldName: column.columnName,
      perTable: [{ table: column.tableName, description: "" }],
    });
    createKeyRef.current += 1;
    setCreateKey(createKeyRef.current);
    setCreateOpen(true);
  };

  return (
    <FieldSectionBody
      fieldDefsQuery={fieldDefsQuery}
      schemaIndex={schemaIndex}
      createOpen={createOpen}
      setCreateOpen={setCreateOpen}
      createInitial={createInitial}
      createKey={createKey}
      editTarget={editTarget}
      setEditTarget={setEditTarget}
      deleteTarget={deleteTarget}
      setDeleteTarget={setDeleteTarget}
      historyTarget={historyTarget}
      setHistoryTarget={setHistoryTarget}
      handleAddClick={handleAddClick}
      handleAddUnmapped={handleAddUnmapped}
      handleCreate={handleCreate}
      handleUpdate={handleUpdate}
      handleDelete={handleDelete}
      createPending={createFieldDef.isPending}
      updatePending={updateFieldDef.isPending}
    />
  );
}
